import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowRight, Github, Sparkles, Zap } from 'lucide-react'
import { AnimatedSection } from '@/components/ui/animated-section'
import {TECH_STACK, AGENTS, MCP_SERVERS, COMMANDS} from '@/utils/constants'

export function Hero() {
  return (
    <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900" />

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 dark:bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-300 dark:bg-blue-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-300 dark:bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <AnimatedSection animation="fade-down">
          <Badge variant="secondary" className="mb-6 text-sm">
            <Sparkles className="w-4 h-4 mr-2" />
            Next-Generation Development Framework
          </Badge>
        </AnimatedSection>

        <AnimatedSection animation="fade-up" delay={100}>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
              SuperCode
            </span>
          </h1>
          <p className="text-xl sm:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            Transform your development workflow with intelligent multi-agent orchestration,
            seamless MCP integrations, and complete project initialization in one command.
          </p>
        </AnimatedSection>

        <AnimatedSection animation="fade-up" delay={200}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button size="lg" className="text-lg px-8 py-6" asChild>
              <a href="#installation">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6" asChild>
              <a href="https://github.com/nx-solutions-ug/supercode" target="_blank" rel="noopener noreferrer">
                <Github className="mr-2 h-5 w-5" />
                View on GitHub
              </a>
            </Button>
          </div>
        </AnimatedSection>

        <AnimatedSection animation="fade-up" delay={300}>
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {TECH_STACK.map((tech, index) => (
              <Badge
                key={tech.name}
                variant="outline"
                className="text-xs sm:text-sm"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <Zap className="w-3 h-3 mr-1" />
                {tech.name}
              </Badge>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection animation="scale-up" delay={400}>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">{ AGENTS.length }</div>
              <div className="text-sm text-muted-foreground">Specialized Agents</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">{ MCP_SERVERS.length }</div>
              <div className="text-sm text-muted-foreground">MCP Integrations</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-teal-600 dark:text-teal-400 mb-2">{ COMMANDS.length }</div>
              <div className="text-sm text-muted-foreground">Commands</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">25+</div>
              <div className="text-sm text-muted-foreground">Behavioral Flags</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">Dual</div>
              <div className="text-sm text-muted-foreground">Memory System</div>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-slate-300 dark:border-slate-600 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-slate-300 dark:bg-slate-600 rounded-full mt-2" />
        </div>
      </div>
    </section>
  )
}
